import type { SavedViewDto, TableDue, TableSort, ViewFilters } from '@werft/contracts';
import { t } from '../../i18n/index.js';
import { newOpId } from '../../platform/api.js';
import { keys, queryClient } from '../../platform/queries.js';
import { outbox } from '../../platform/sync.js';
import { toast } from '../../platform/toast.js';

/**
 * Gespeicherte Ansichten (P25, ADR 0028): Filter der Tabelle unter einem Namen. „me“ steht für die Person, die die
 * Ansicht öffnet – so passt „Meine Aufgaben“ für alle. Eingebaut: „Alle offenen“ und „Meine Aufgaben“.
 */
export interface Filters {
  sort: TableSort;
  dir: 'asc' | 'desc';
  done: boolean;
  status: string;
  assignee: string;
  label: string;
  priority: string;
  due: '' | TableDue;
  field: string;
  milestones: boolean;
  /** Audit 0.36 F07: nur blockierte bzw. dringende (Einstieg aus „Braucht Aufmerksamkeit“) */
  blocked: boolean;
  urgent: boolean;
}
export const BLANK: Filters = { sort: 'position', dir: 'asc', done: false, status: '', assignee: '', label: '', priority: '', due: '', field: '', milestones: false, blocked: false, urgent: false };
const FILTER_KEYS = Object.keys(BLANK) as (keyof Filters)[];

export const BUILTIN = { all: 'alle', mine: 'meine' } as const;
export const builtinFilters = (id: string): ViewFilters | null => (id === BUILTIN.all ? {} : id === BUILTIN.mine ? { assignee: 'me' } : null);

/** Gespeicherte Filter → aktuelle Tabellen-Filter (fehlende Werte = leer, „me“ = ich) */
export function toPrefs(f: ViewFilters, me: string): Filters {
  const out: Filters = { ...BLANK };
  for (const k of FILTER_KEYS) if (f[k] !== undefined) (out as unknown as Record<string, unknown>)[k] = f[k];
  if (out.assignee === 'me') out.assignee = me;
  return out;
}

/** Aktuelle Filter → speicherbare Form (nur Gesetztes; ich = „me“) */
export function fromPrefs(p: Filters, me: string): ViewFilters {
  const out: Record<string, unknown> = {};
  for (const k of FILTER_KEYS) if (p[k] !== BLANK[k]) out[k] = k === 'assignee' && p[k] === me ? 'me' : p[k];
  return out as ViewFilters;
}

export const sameFilters = (a: Filters, b: Filters) => FILTER_KEYS.every((k) => a[k] === b[k]);

/** Schränken die Filter den Umfang ein? (Sortierung zählt nicht – Audit F16) */
export const narrows = (p: Partial<Filters>) => FILTER_KEYS.some((k) => k !== 'sort' && k !== 'dir' && p[k] !== undefined && p[k] !== BLANK[k]);

/* ---------- Schreibwege (Ausgangskorb) ---------- */

const failed = (projectId: string) => (_e: unknown, err: unknown) => {
  void queryClient.invalidateQueries({ queryKey: keys.views(projectId) });
  toast(t('sync.failed', { message: err instanceof Error ? err.message : t('task.failed') }), { kind: 'error' });
};
const setViews = (projectId: string, fn: (items: SavedViewDto[]) => SavedViewDto[]) =>
  queryClient.setQueryData<{ items: SavedViewDto[] }>(keys.views(projectId), (old) => ({ items: fn(old?.items ?? []) }));

outbox.register('view.create', {
  done: (e, r) => {
    const temp = (e.meta as { temp: string }).temp;
    setViews(e.projectId, (items) => [...items.filter((v) => v.id !== temp), r as SavedViewDto]);
    // die Tabelle tauscht die vorläufige ID gegen die echte
    window.dispatchEvent(new CustomEvent('werft:view-saved', { detail: { temp, id: (r as SavedViewDto).id } }));
  },
  fail: (e, err) => failed(e.projectId)(e, err),
});
outbox.register('view.update', {
  done: (e, r) => setViews(e.projectId, (items) => items.map((v) => (v.id === (r as SavedViewDto).id ? (r as SavedViewDto) : v))),
  fail: (e, err) => failed(e.projectId)(e, err),
});
outbox.register('view.remove', { done: () => {}, fail: (e, err) => failed(e.projectId)(e, err) });

/** Neue Ansicht; gibt die vorläufige ID zurück (die echte kommt nach dem Speichern) */
export function createView(projectId: string, me: string, name: string, filters: ViewFilters, shared: boolean): string {
  const opId = newOpId();
  const temp = `tmp-${opId}`;
  setViews(projectId, (items) => [...items, { id: temp, projectId, name, filters, shared, ownerId: me, mine: true, version: 0 }]);
  void outbox.enqueue({ opId, kind: 'view.create', projectId, method: 'POST', url: `/api/projects/${projectId}/views`, body: { opId, name, filters, shared }, meta: { temp } }).catch(() => {});
  return temp;
}

export function updateView(v: SavedViewDto, patch: { name?: string; filters?: ViewFilters; shared?: boolean }) {
  if (v.id.startsWith('tmp-')) return;
  const opId = newOpId();
  setViews(v.projectId, (items) => items.map((x) => (x.id === v.id ? { ...x, ...patch } : x)));
  void outbox.enqueue({ opId, kind: 'view.update', projectId: v.projectId, method: 'PATCH', url: `/api/views/${v.id}`, body: { opId, expectedVersion: v.version, patch } }).catch(() => {});
}

export function removeView(v: SavedViewDto) {
  if (v.id.startsWith('tmp-')) return;
  const opId = newOpId();
  setViews(v.projectId, (items) => items.filter((x) => x.id !== v.id));
  void outbox.enqueue({ opId, kind: 'view.remove', projectId: v.projectId, method: 'DELETE', url: `/api/views/${v.id}`, body: { opId } }).catch(() => {});
  toast(t('views.deleted', { name: v.name }));
}
